import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Leaf, TrendingUp, Zap, Package, Truck,
  Check, ArrowRight,
  BarChart3, AlertCircle, Map,
  ChevronDown, Rocket, Heart, Sparkles,
  AlertTriangle, Clock, Shield, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { apiGet } from "@/lib/api";
import { ScopeEngine } from "@/components/dashboard/ScopeEngine";
import { Footer } from "@/components/Footer";
import { CommunityTicker } from "@/components/CommunityTicker";
import { CompanyLogo } from "@/components/CompanyLogo";

const DRIVER_ICONS = { "Materials & Sourcing": Package, "Energy Usage": Zap, "Logistics": Truck };

const MILESTONES = [
  { pct: 20, label: "Foundation" },
  { pct: 40, label: "Understanding" },
  { pct: 60, label: "Clarity" },
  { pct: 80, label: "Ready" },
  { pct: 100, label: "Complete" },
];

function ReadinessRing({ score }) {
  const r = 60, c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <div className="relative w-40 h-40 mx-auto">
      <svg className="readiness-ring w-full h-full" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={r} fill="none" stroke="hsl(141 15% 14%)" strokeWidth="8" />
        <circle cx="70" cy="70" r={r} fill="none" stroke="hsl(142 69% 58%)" strokeWidth="8" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{score}%</span>
        <span className="text-xs text-muted-foreground uppercase tracking-wider">Ready</span>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { companyId } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [warningExpanded, setWarningExpanded] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const data = await apiGet(`/api/dashboard/${companyId}`);
          if (!mountedRef.current) return;
          setDashboard(data);
          setLoading(false);
          return;
        } catch (err) {
          if (!mountedRef.current) return;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
        }
      }
      if (mountedRef.current) {
        setError("Could not load dashboard. The company may not exist.");
        setLoading(false);
      }
    };
    load();
    return () => { mountedRef.current = false; };
  }, [companyId]);

  // Show scroll hint once dashboard is loaded, hide on scroll
  useEffect(() => {
    if (!dashboard) return;
    const timer = setTimeout(() => setShowScrollHint(true), 800);
    const handleScroll = () => { setShowScrollHint(false); };
    window.addEventListener("scroll", handleScroll, { once: true });
    return () => { clearTimeout(timer); window.removeEventListener("scroll", handleScroll); };
  }, [dashboard]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard unavailable</h2>
          <p className="text-muted-foreground mb-4">{error || "No data found for this company. Please complete the onboarding first."}</p>
          <Button onClick={() => window.location.href = "/"} className="rounded-full bg-primary text-primary-foreground">
            Start onboarding
          </Button>
        </div>
      </div>
    );
  }

  const { company, readiness_score, progress, impact_overview, market_intelligence } = dashboard;
  const mi = market_intelligence || {};

  return (
    <div className="flex flex-col min-h-screen aurora-glow" data-testid="dashboard-container">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => {
                localStorage.removeItem('olana_company_id');
                localStorage.removeItem('olana_user_id');
                localStorage.removeItem('olana_email');
                window.location.href = '/';
              }}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
              data-testid="logo-home-link"
            >
              <Leaf className="w-5 h-5 text-primary" />
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Olana</span>
            </button>
            <Separator orientation="vertical" className="h-5 bg-border/50 mx-2" />
            <CompanyLogo name={company?.name} logoUrl={company?.logo_url} />
            <span className="text-white font-semibold text-sm" data-testid="header-company-name">{company?.name}</span>
            {company?.country && (
              <span className="text-white/40 text-xs">&middot; {company.country}</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <Link to={`/impact-map/${companyId}`} className="text-sm text-muted-foreground hover:text-white transition-colors flex items-center gap-1.5" data-testid="nav-impact-map">
              <Map className="w-3.5 h-3.5" /> Impact Map
            </Link>
            <Badge className="bg-primary/10 text-primary border-primary/20">
              {readiness_score}% Ready
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-12 space-y-8 w-full">
        {/* Community Ticker - Context-aware based on company profile */}
        <CommunityTicker 
          industry={company?.industry || "general"} 
          country={company?.country || ""}
          city={company?.city || ""}
          size={company?.size || ""}
          companyId={companyId}
        />

        {/* Title */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Dashboard</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Your Sustainability Profile
          </h1>
        </motion.div>

        {/* Progress — directly below title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="glass-card p-6 md:p-8"
          data-testid="progress-section"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-8 rounded-full bg-primary" />
            <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Your Progress</h3>
          </div>
          <Progress value={progress} className="h-2 bg-muted mb-4" data-testid="progress-bar" />
          <div className="flex justify-between">
            {MILESTONES.map((m) => (
              <div key={m.pct} className="flex flex-col items-center gap-1.5">
                <div className={`milestone-dot ${progress >= m.pct ? 'active' : 'inactive'}`} />
                <span className={`text-xs ${progress >= m.pct ? 'text-primary' : 'text-muted-foreground'}`}>{m.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {/* Impact Overview - spans 2 cols */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6 md:p-8 md:col-span-2 lg:col-span-2"
            data-testid="impact-overview"
          >
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Impact Overview</h3>
            </div>
            <div className="mb-4">
              <span className="text-sm text-muted-foreground">Estimated carbon footprint</span>
              <p className="text-2xl font-bold text-white mt-1" data-testid="footprint-estimate">{impact_overview.estimated_footprint}</p>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {(impact_overview.key_drivers || []).map((driver) => {
                const DIcon = DRIVER_ICONS[driver] || Zap;
                return (
                  <div key={driver} className="flex items-center gap-1.5 bg-white/5 rounded-full px-3 py-1.5 text-sm text-white/60">
                    <DIcon className="w-3.5 h-3.5" />
                    {driver}
                  </div>
                );
              })}
            </div>
            <p className="text-white/50 leading-relaxed" data-testid="impact-insight">{impact_overview.insight}</p>
          </motion.div>

          {/* Readiness Score */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6 md:p-8 flex flex-col items-center justify-center"
            data-testid="readiness-score"
          >
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4">Readiness</h3>
            <ReadinessRing score={readiness_score} />
          </motion.div>

          {/* Insight Strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card glass-card-selected p-6 md:p-8 flex flex-col justify-center"
            data-testid="insight-strip"
          >
            <TrendingUp className="w-6 h-6 text-primary mb-3" />
            <p className="text-lg text-white font-medium leading-relaxed">{impact_overview.insight}</p>
          </motion.div>
        </div>

        {/* === COMPLIANCE INTELLIGENCE BLOCK === */}
        {mi?.urgency_message && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32 }}
            className="rounded-2xl border border-red-500/40 overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(2,4,2,0.98) 100%)" }}
            data-testid="urgency-banner"
          >
            <div
              className="p-5 cursor-pointer"
              onClick={() => setWarningExpanded(!warningExpanded)}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-red-300 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Your compliance window is closing
                    </h4>
                    <p className="text-white/70 text-sm">
                      Companies without verified sustainability data face <strong className="text-white">immediate business risks</strong>.
                    </p>
                  </div>
                </div>
                <motion.div animate={{ rotate: warningExpanded ? 180 : 0 }} transition={{ duration: 0.2 }} className="mt-2">
                  <ChevronDown className="w-5 h-5 text-white/40" />
                </motion.div>
              </div>

              <div className="mt-4 pl-13 space-y-2">
                {(mi.risks || []).map((risk, i) => (
                  <div key={`risk-${risk.keyword}-${i}`} className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0"></span>
                    <span className="text-white/80"><strong className="text-red-300">{risk.keyword}</strong> — {risk.detail}</span>
                  </div>
                ))}
              </div>
            </div>

            <AnimatePresence>
              {warningExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="border-t border-white/10"
                >
                  <div className="p-5 space-y-5">
                    {mi.happening_now && mi.happening_now.length > 0 && (
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-amber-300 font-semibold text-sm uppercase tracking-wide mb-1">This is happening now</p>
                          <div className="space-y-2">
                            {mi.happening_now.map((item, i) => (
                              <p key={`hn-${i}-${item.text?.slice(0,20)}`} className="text-white/70 text-sm leading-relaxed">{item.text}</p>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {mi.leaders_doing && (
                      <div className="flex items-start gap-3">
                        <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-primary font-semibold text-sm uppercase tracking-wide mb-1">What leaders are doing</p>
                          <p className="text-white/70 text-sm leading-relaxed">
                            <strong className="text-white">{mi.leaders_doing.names}</strong> already {mi.leaders_doing.action}. They're winning contracts and trust — while laggards lose both.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-primary flex-shrink-0" />
                        <p className="text-white text-sm">
                          <strong>Act now:</strong> Build your sustainability profile today.<br />
                          <span className="text-white/50">Delay:</span>{" "}
                          <span className="text-red-300">{mi.act_now_consequence || "Risk losing contracts, market access, and buyer trust."}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {/* === IMPACT MAP CTA — prominent hero card === */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <Link to={`/impact-map/${companyId}`} className="block group" data-testid="impact-map-cta">
            <div className="relative overflow-hidden rounded-2xl border border-primary/25 transition-all duration-500 group-hover:border-primary/50 group-hover:shadow-[0_0_60px_rgba(74,222,128,0.12)]"
              style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.06) 0%, rgba(2,4,2,0.95) 40%, rgba(2,4,2,0.95) 60%, rgba(74,222,128,0.04) 100%)" }}>
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary/[0.04] blur-[80px] group-hover:bg-primary/[0.08] transition-all duration-700" />
              <div className="relative p-6 md:p-8 flex items-center gap-6">
                <div className="shrink-0 w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/15 group-hover:scale-110 transition-all duration-300">
                  <Map className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      View Impact Map
                    </h3>
                    <Badge className="bg-primary/15 text-primary border-primary/25 text-[10px] px-2 py-0 animate-pulse">
                      LIVE
                    </Badge>
                  </div>
                  <p className="text-sm text-white/50 leading-relaxed">
                    Interactive heatmap + Doughnut Economics boundaries — see what to fix, why, and how urgent it is
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-primary/60 group-hover:text-primary group-hover:translate-x-2 transition-all duration-300 shrink-0" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* === ACTION HUB CTA === */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <Link to={`/action-hub/${companyId}`} className="block group" data-testid="action-hub-cta">
            <div className="relative overflow-hidden rounded-2xl border border-primary/25 transition-all duration-500 group-hover:border-primary/50 group-hover:shadow-[0_0_60px_rgba(74,222,128,0.12)]"
              style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.06) 0%, rgba(2,4,2,0.95) 40%, rgba(2,4,2,0.95) 60%, rgba(74,222,128,0.04) 100%)" }}>
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary/[0.04] blur-[80px] group-hover:bg-primary/[0.08] transition-all duration-700" />
              <div className="relative p-6 md:p-8 flex items-center gap-6">
                <div className="shrink-0 w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/15 group-hover:scale-110 transition-all duration-300">
                  <Rocket className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Action Hub
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    Engage stakeholders, explore funding support, and act on your top priorities
                  </p>
                </div>
                <ArrowRight className="w-6 h-6 text-primary/60 group-hover:text-primary group-hover:translate-x-2 transition-all duration-300 shrink-0" />
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Scope Engine — no fade-in delay so loading state shows immediately */}
        <div>
          <ScopeEngine companyId={companyId} />
        </div>

        {/* === HIDDEN POTENTIALS CTA === */}
        {!showThankYou ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div
              onClick={() => setShowThankYou(true)}
              className="relative overflow-hidden rounded-2xl border border-amber-500/15 cursor-pointer transition-all duration-500 hover:border-amber-500/35 hover:shadow-[0_0_50px_rgba(245,158,11,0.06)] group"
              style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.04) 0%, rgba(2,4,2,0.95) 50%, rgba(74,222,128,0.03) 100%)" }}
              data-testid="hidden-potentials-cta"
            >
              <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full bg-amber-500/[0.03] blur-[80px] group-hover:bg-amber-500/[0.06] transition-all duration-700" />
              <div className="relative p-6 md:p-8 flex items-center gap-6">
                <div className="shrink-0 w-16 h-16 rounded-2xl bg-amber-500/[0.06] border border-amber-500/15 flex items-center justify-center group-hover:bg-amber-500/10 group-hover:scale-110 transition-all duration-300">
                  <Sparkles className="w-8 h-8 text-amber-400/70 group-hover:text-amber-400 transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Unlock Your Hidden Potential
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    Let us help you leverage untapped opportunities towards the betterment of our planet
                  </p>
                </div>
                <Heart className="w-6 h-6 text-amber-400/30 group-hover:text-amber-400 group-hover:scale-110 transition-all duration-300 shrink-0" />
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card glass-card-selected p-8 md:p-12 text-center"
            data-testid="thank-you-message"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 300, delay: 0.2 }}
            >
              <Check className="w-14 h-14 text-primary mx-auto mb-4" />
            </motion.div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Thank You!
            </h3>
            <p className="text-white/60 text-lg leading-relaxed max-w-lg mx-auto">
              Your response has been recorded. We will get in touch with you soon to help you unlock your sustainability potential.
            </p>
          </motion.div>
        )}

        {/* Completion message */}
        {progress >= 80 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-card glass-card-selected p-8 text-center"
            data-testid="completion-message"
          >
            <Check className="w-10 h-10 text-primary mx-auto mb-3" />
            <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              You've improved your sustainability profile
            </h3>
            <p className="text-white/50">You're now ready for the next step in your sustainability journey.</p>
          </motion.div>
        )}
      </div>

      {/* Footer - pinned at bottom */}
      <div className="shrink-0 mt-auto">
        <Footer />
      </div>

      {/* Scroll-down indicator */}
      <AnimatePresence>
        {showScrollHint && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.4 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center cursor-pointer"
            onClick={() => { window.scrollBy({ top: 400, behavior: 'smooth' }); setShowScrollHint(false); }}
            data-testid="scroll-hint"
          >
            <span className="text-xs text-white/50 mb-1 font-medium tracking-wider uppercase">Scroll down</span>
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-10 h-10 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center backdrop-blur-sm"
            >
              <ChevronDown className="w-5 h-5 text-primary" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
